import { parseHours } from './hours';
import { esc, html, toast } from './ui';

/**
 * Dialogs.
 *
 * Every write in this application happens in one, and they all behave the
 * same way: a native <dialog> so the browser handles focus trapping, Escape
 * and the backdrop; a body that can re-render itself as the user changes the
 * inputs it depends on; and a footer whose primary button is the only place
 * that submits.
 *
 * The submit path is the part worth being careful about. A slow network on a
 * ramp is the normal case, not the exception, so the primary button disables
 * itself for the whole round trip and the error comes back into the dialog
 * rather than a toast that disappears before it is read — the user still has
 * the form in front of them and usually has to change something in it.
 */

export interface DialogSpec<T> {
  title: string;
  /** One line under the title saying why this dialog exists. */
  sub?: string;
  wide?: boolean;
  /**
   * The submit button's label. An empty string means there is nothing to
   * submit — the dialog is a reader, and its footer carries only Close.
   */
  primary: string;
  /** Rendered into the body; re-run whenever `refresh()` is called. */
  body: () => string;
  /** Wired after every render — attach listeners here, not in `body`. */
  onRender?: (root: HTMLElement, refresh: () => void) => void;
  /** Reads the form and returns the payload, or a message explaining what is missing. */
  collect: (root: HTMLElement) => T | { error: string };
  submit: (payload: T) => Promise<unknown>;
  /** The toast shown on success. */
  done: (result: any) => string;
  /** Called after a successful submit, e.g. to reload the view behind it. */
  onDone?: (result: any) => void;
  /**
   * Called once the dialog is gone, saying whether it submitted.
   *
   * For a form that did something before Save — uploaded a file, say — and
   * has to undo it if the person changes their mind. Runs on Cancel, on the
   * close button, on Escape, and after a successful submit with `true`.
   */
  onClose?: (submitted: boolean) => void;
}

/**
 * The dialogs currently on screen, innermost last.
 *
 * A stack rather than a single dialog, because some dialogs legitimately open
 * another on top: the work pack picks its tasks from the maintenance
 * programme in a second dialog and comes back with them. This used to be one
 * element, and opening a dialog closed whatever was underneath — so "Add from
 * AMP" silently destroyed the half-filled work pack behind it, and pressing
 * "Add the ticked tasks" returned to nothing. Everything typed was gone and no
 * pack was ever created.
 *
 * Native <dialog> stacks in the top layer on its own, so the browser still
 * handles focus and Escape for whichever is in front.
 */
const stack: HTMLDialogElement[] = [];

/**
 * What to tell each dialog when it goes.
 *
 * Called from `close()` rather than from the element's own `close` event: that
 * event is queued, and the element is removed from the document in the same
 * breath, so in practice it never arrives. A form that uploaded a file has to
 * be told the person walked away, and "in practice never" is not a way of
 * telling it.
 */
const onClosed = new WeakMap<HTMLDialogElement, () => void>();

export function openDialog<T>(spec: DialogSpec<T>): void {
  const dlg = document.createElement('dialog');
  dlg.className = spec.wide ? 'dlg wide' : 'dlg';
  dlg.innerHTML = html`
    <form method="dialog" class="dlg-form" novalidate>
      <header class="dlg-h">
        <div>
          <h2>${esc(spec.title)}</h2>
          ${spec.sub ? html`<p class="tiny dim">${esc(spec.sub)}</p>` : ''}
        </div>
        <button type="button" class="x" data-close aria-label="Close">✕</button>
      </header>
      <div class="dlg-b" data-body></div>
      <footer class="dlg-f">
        <div data-error style="color:var(--exceed);font-size:12.5px;max-width:52ch"></div>
        <span class="sp"></span>
        <button type="button" class="btn" data-close>${spec.primary ? 'Cancel' : 'Close'}</button>
        ${spec.primary
          ? html`<button type="button" class="btn pri" data-submit>${esc(spec.primary)}</button>`
          : ''}
      </footer>
    </form>
  `;

  let submitted = false;
  onClosed.set(dlg, () => spec.onClose?.(submitted));

  const body = dlg.querySelector<HTMLElement>('[data-body]')!;
  const errorBox = dlg.querySelector<HTMLElement>('[data-error]')!;
  // Absent on a dialog with nothing to submit — see DialogSpec.primary.
  const primary = dlg.querySelector<HTMLButtonElement>('[data-submit]');

  /**
   * A refresh rebuilds the body from scratch, which would otherwise throw away
   * everything already typed — change the regime after filling in half the form
   * and the registration, serial number and counters all empty themselves.
   *
   * Typed values are restored; dropdowns are not. A `<select>` is rendered from
   * state the dialog is deliberately holding — the regime list narrows with the
   * register, the programme bases follow the regime — so the fresh markup's
   * selection is the correct one, and putting the old value back would undo the
   * very thing the refresh was for.
   */
  const TYPED = 'input[type=text], input[type=number], input[type=date], input[type=email], input:not([type]), textarea';

  const refresh = () => {
    const kept = new Map<string, string>();
    body.querySelectorAll<HTMLInputElement>(TYPED).forEach((el) => {
      if (el.name && el.value) kept.set(el.name, el.value);
    });

    body.innerHTML = spec.body();

    body.querySelectorAll<HTMLInputElement>(TYPED).forEach((el) => {
      const previous = kept.get(el.name);
      if (previous !== undefined) el.value = previous;
    });

    spec.onRender?.(body, refresh);
  };

  const showError = (message: string) => {
    errorBox.textContent = message;
  };

  dlg.querySelectorAll('[data-close]').forEach((b) =>
    b.addEventListener('click', () => close()),
  );

  primary?.addEventListener('click', async () => {
    showError('');
    const collected = spec.collect(body);
    if (collected && typeof collected === 'object' && 'error' in collected) {
      showError((collected as { error: string }).error);
      return;
    }

    const label = primary!.textContent;
    primary!.disabled = true;
    primary!.textContent = 'Working…';
    try {
      const result = await spec.submit(collected as T);
      submitted = true;
      close();
      toast(spec.done(result));
      spec.onDone?.(result);
    } catch (e) {
      // Back into the dialog, not a toast: the form is still on screen and the
      // fix is almost always in it.
      showError(e instanceof Error ? e.message : 'That did not go through. Try again.');
      primary!.disabled = false;
      primary!.textContent = label;
    }
  });

  // Escape fires `cancel` on a native dialog; clean up rather than leaving the
  // element in the document.
  dlg.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  document.body.appendChild(dlg);
  stack.push(dlg);
  refresh();
  dlg.showModal();
  dlg.querySelector<HTMLElement>('input,select,textarea')?.focus();
}

/**
 * Close the dialog in front.
 *
 * Only the top one: anything underneath is a form somebody was part way
 * through, and closing it would throw their work away. Callers that mean
 * "replace this dialog with another" already call this first and then open —
 * which behaves exactly as it did.
 */
export function close(): void {
  const dlg = stack.pop();
  if (!dlg) return;
  dlg.close();
  dlg.remove();
  onClosed.get(dlg)?.();
}

// ── form building blocks, matching the mockup's markup ─────────────────────

export const field = (label: string, inner: string, hint?: string): string => html`
  <div class="field">
    <label>${esc(label)}</label>
    ${inner}
    ${hint ? html`<span class="hint">${hint}</span>` : ''}
  </div>
`;

export const input = (name: string, value: string | number = '', extra = ''): string =>
  `<input name="${esc(name)}" value="${esc(value)}" ${extra}>`;

export const select = (name: string, options: string, extra = ''): string =>
  `<select name="${esc(name)}" ${extra}>${options}</select>`;

export const opt = (value: string, label = value, selected = false): string =>
  `<option value="${esc(value)}"${selected ? ' selected' : ''}>${esc(label)}</option>`;

export const callout = (tone: 'ok' | 'info' | 'caution' | 'exceed', glyph: string, body: string): string =>
  html`<div class="callout c-${tone}"><span class="cg" aria-hidden="true">${esc(glyph)}</span><div>${body}</div></div>`;

/** Reads a named control out of a rendered dialog body. */
export const val = (root: HTMLElement, name: string): string =>
  (root.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`)?.value ?? '').trim();

export const numVal = (root: HTMLElement, name: string): number | undefined => {
  const raw = val(root, name);
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * An hours box, read back in whichever convention it was typed in.
 *
 * `numVal` would read "3411:54" as not-a-number and hand back `undefined`,
 * which every collect() in the application reports as a missing field — so an
 * organisation set to hours and minutes would be told its airframe hours were
 * blank while looking straight at them. This is the one place that knows a
 * colon is a legitimate way of writing an hour figure.
 *
 * Both forms are accepted whatever the setting says; see `hours.ts`. Used for a
 * reading or a duration. A limit, an interval, a tolerance or man-hours stays
 * on `numVal`, because those are decimal by convention wherever they are
 * quoted from.
 */
export const hoursVal = (root: HTMLElement, name: string): number | undefined =>
  parseHours(val(root, name));

export const todayISO = (): string => new Date().toISOString().slice(0, 10);
